import { Card, Grid, NumberInput, Radio, Text } from '@mantine/core';
import LabeledSlider from '../LabeledSlider';
import { useEffect, useState } from 'react';

const AdvancedImagePromptSettings = ({ advancedSettings, setAdvancedSettings }) => {
  const [aspectRatioType, setAspectRatioType] = useState(
    advancedSettings.aspect_ratio === 'custom' ? 'custom' : 'predefined'
  );

  const aspect_ratios = ['1:1', '16:9', '21:9', '2:3', '3:2', '4:5', '5:4', '9:16', '9:21'];

  useEffect(() => {
    if (aspectRatioType === 'predefined' && !advancedSettings.aspect_ratio) {
      setAdvancedSettings({ ...advancedSettings, aspect_ratio: '1:1' });
    }
  }, [aspectRatioType, advancedSettings, setAdvancedSettings]);

  const handleAspectRatioSelect = (ratio) => {
    setAdvancedSettings({ ...advancedSettings, aspect_ratio: ratio });
  };

  const handleRadioChange = (type) => {
    setAspectRatioType(type);
    if (type === 'custom') {
      const { ...updatedSettings } = advancedSettings;
      setAdvancedSettings({ ...updatedSettings, width: '', height: '' });
    } else {
      handleAspectRatioSelect('1:1');
    }
  };

  // Safe image generation is hidden for now, but it is possible to be able to use it in the future.
  return (
    <Card.Section inheritPadding py="sm">
      {/* <Grid align="center" mb="sm">
        <Grid.Col span={{ base: 12, sm: 2 }}>
          <Text fw={500}>Safe Image Generation</Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 10 }} pt="md">
          <Checkbox
            onChange={(e) => setAdvancedSettings({ ...advancedSettings, safeImageGeneration: e.target.checked })}
            checked={advancedSettings.safeImageGeneration}
          />
          <Text size="sm" c="dimmed">
            <strong>Safe image generation avoids harmful, offensive, or inappropriate content</strong>.
          </Text>
        </Grid.Col>
      </Grid> */}
      <Grid align="center" mb="sm">
        <Grid.Col span={{ base: 12, sm: 2 }}>
          <Text fw={500}>Prompt Strength</Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 10 }} pt="md">
          <LabeledSlider
            min={1}
            max={100}
            step={1}
            value={advancedSettings.prompt_strength}
            onChange={(str) => setAdvancedSettings({ ...advancedSettings, prompt_strength: str })}
          />
          <Text size="sm" c="dimmed" mt="xs">
            <strong>Prompt Strength</strong> regulates the extent to which AI follows the given instructions. A higher
            setting results in strict adherence, while a lower setting permits greater creative expression.
          </Text>
        </Grid.Col>
      </Grid>
      <Grid align="center" mb="sm">
        <Grid.Col span={{ base: 12, sm: 2 }}>
          <Text fw={500}>Seed</Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 10 }}>
          <NumberInput
            value={advancedSettings.seed}
            onChange={(value) => {
              if (value >= 0 && value <= 4294967295) {
                setAdvancedSettings({ ...advancedSettings, seed: value });
              }
            }}
            placeholder="Enter a seed number"
            min={0}
            max={4294967295}
          />
          <Text size="sm" c="dimmed" mt="xs">
            <strong>Minimum value 0.</strong> <strong>Maximum value 4294967295.</strong>
            <br />
            <strong>Seed</strong> serves as a predetermined starting point for AI in generating an image. Using the same
            seed with the same instructions ensures repeatability, producing identical results under the same
            conditions. Different seeds initiate the process differently, leading to varied outcomes.
          </Text>
        </Grid.Col>
      </Grid>
      <Grid align="flex-start" mb="sm">
        <Grid.Col span={{ base: 12, sm: 2 }}>
          <Text fw={500}>Aspect Ratio</Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 10 }}>
          <Radio.Group value={aspectRatioType} onChange={handleRadioChange} mb="sm">
            <Radio value="predefined" label="Predefined" mr="md" />
            <Radio value="custom" label="Custom" />
          </Radio.Group>

          {aspectRatioType === 'predefined' && (
            <div>
              {aspect_ratios.map((ratio) => (
                <button
                  key={ratio}
                  type="button"
                  className={`p15 button-element ${
                    advancedSettings.aspect_ratio === ratio ? 'active' : ''
                  }`}
                  style={{ marginTop: 4, marginBottom: 0, marginRight: 8, color: 'var(--mantine-primary-color-filled)' }}
                  onClick={() => handleAspectRatioSelect(ratio)}
                >
                  {ratio}
                </button>
              ))}
            </div>
          )}

          {aspectRatioType === 'custom' && (
            <Grid align="center">
              <Grid.Col span={{ base: 12, sm: 5 }}>
                <NumberInput
                  value={advancedSettings.width || ''}
                  placeholder="Width"
                  onChange={(value) => setAdvancedSettings({ ...advancedSettings, width: value || '' })}
                  min={1}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 5 }}>
                <NumberInput
                  value={advancedSettings.height || ''}
                  placeholder="Height"
                  onChange={(value) => setAdvancedSettings({ ...advancedSettings, height: value || '' })}
                  min={1}
                />
              </Grid.Col>
            </Grid>
          )}
          <Text size="sm" c="dimmed" mt="xs">
            For best results, we recommend using the <strong>predefined</strong> aspect ratios.
          </Text>
        </Grid.Col>
      </Grid>
    </Card.Section>
  );
};

export default AdvancedImagePromptSettings;
